import React, { useCallback, useState } from 'react';
import { View, Pressable, StyleSheet, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import ExploreCardImage from './ExploreCardImage';
import ExploreCardVideo from './ExploreCardVideo';
import ExploreCardMusic from './ExploreCardMusic';
import ExploreCard3D from './ExploreCard3D';
import ExploreCardQuickActions from './ExploreCardQuickActions';
import ExploreCardDropdown from '@/components/Dropdowns/ExploreCardDropdown';
import { getExploreItemAspectRatio, getExploreItemRawAspectRatio } from './exploreMasonryLayout';

const CARD_GAP = 1;
const isWeb = Platform.OS === 'web';
const isNative = Platform.OS === 'ios' || Platform.OS === 'android';

// True only on devices with a fine pointing device (mouse / trackpad).
// On touch-only devices (mobile web Safari/Chrome) we skip hover handlers
// entirely — otherwise the synthetic `mouseenter` fired on first tap
// suppresses the subsequent click and the user has to tap twice to
// open the lightbox. Computed once at module load; the result doesn't
// change for the lifetime of the page.
const SUPPORTS_HOVER = (() => {
  if (!isWeb || typeof window === 'undefined' || !window.matchMedia) return false;
  try {
    return window.matchMedia('(hover: hover)').matches;
  } catch (_) {
    return false;
  }
})();

const ExploreCard = React.memo(({
  item, onPress, onLongPress, isFocused, squareTile = false, squareSize = 132, feedMode = false, feedWidth,
}) => {
  const { colors } = useTheme();
  const [hovered, setHovered] = useState(false);

  const handlePress = useCallback((e) => {
    // Music cards contain their own play/pause control. Ignore card presses
    // that originate inside it so toggling a preview never opens the lightbox.
    if (isWeb) {
      const target = e?.nativeEvent?.target;
      if (target && typeof target.closest === 'function' && target.closest('[data-explore-music-play]')) {
        return;
      }
    }
    onPress?.(item);
  }, [item, onPress]);

  const aspectRatio = squareTile
    ? 1
    : (feedMode
      ? getExploreItemAspectRatio(item, 'list')
      : getExploreItemRawAspectRatio(item));

  const cardSizeStyle = squareTile && squareSize
    ? { width: squareSize, height: squareSize, maxWidth: squareSize, maxHeight: squareSize }
    : null;

  const cardContent = (
    <View
      onMouseEnter={SUPPORTS_HOVER ? () => setHovered(true) : undefined}
      onMouseLeave={SUPPORTS_HOVER ? () => setHovered(false) : undefined}
      style={[
        styles.card,
        feedMode && styles.cardFeed,
        {
          aspectRatio,
          backgroundColor: colors.solid_color_tertiary,
          ...cardSizeStyle,
        },
        isWeb && isFocused && { outline: `2px solid ${colors.text_color_primary}` },
      ]}
    >
      <Pressable onPress={handlePress} style={styles.pressable}>
        <View style={styles.inner}>
          {item.mediaType === 'image' && (
            <ExploreCardImage item={item} hovered={hovered} />
          )}
          {item.mediaType === 'video' && (
            <ExploreCardVideo item={item} hovered={hovered} />
          )}
          {item.mediaType === 'music' && (
            <ExploreCardMusic item={item} hovered={hovered} />
          )}
          {item.mediaType === '3d' && (
            <ExploreCard3D item={item} hovered={hovered} />
          )}
        </View>
      </Pressable>
      {SUPPORTS_HOVER && hovered && (
        <ExploreCardQuickActions item={item} />
      )}
    </View>
  );

  return (
    <View style={[
      styles.wrapper,
      feedMode && styles.wrapperFeed,
      feedMode && feedWidth ? { width: feedWidth, maxWidth: '100%' } : null,
      squareTile && squareSize ? { width: squareSize + CARD_GAP, alignItems: 'center' } : null,
    ]}
    >
      {isNative ? (
        <ExploreCardDropdown item={item}>
          {cardContent}
        </ExploreCardDropdown>
      ) : cardContent}
    </View>
  );
});

const styles = StyleSheet.create({
  wrapper: {
    padding: CARD_GAP / 2,
  },
  wrapperFeed: {
    paddingVertical: 6,
    paddingHorizontal: 0,
    width: '100%',
  },
  card: {
    width: '100%',
    overflow: 'hidden',
    ...(isWeb ? { cursor: 'pointer' } : {}),
  },
  cardFeed: {
    borderRadius: 10,
  },
  pressable: {
    flex: 1,
  },
  inner: {
    flex: 1,
    overflow: 'hidden',
  },
});

export default ExploreCard;
